import { DownloadIcon, XIcon } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { FileRef } from '@/shared/api/types';
import { useFormatters } from '@/shared/i18n/format';
import { cn } from '@/shared/lib/cn';
import { getFileKind } from '@/shared/lib/files';

import { FileIcon } from './FileIcon';
import { FilePreviewDialog } from './FilePreviewDialog';
import { FileScanBadge } from './FileScanBadge';
import { toDownloadUrl } from './urls';

export interface AttachmentListProps {
  files: readonly FileRef[];
  /** Shows a remove button on every attachment (editing mode). */
  onRemove?: (file: FileRef) => void;
  /** `grid` – image thumbnails + file cards; `list` – compact rows. */
  layout?: 'grid' | 'list';
  className?: string;
}

/** Attachments with thumbnails / type icons, size, download and an in-app preview (images, PDF, media, text). */
export function AttachmentList({ files, onRemove, layout = 'grid', className }: AttachmentListProps) {
  const { t } = useTranslation('shared');
  const fmt = useFormatters();
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);
  if (files.length === 0) return null;

  const images = files.filter((f) => getFileKind(f.name, f.contentType) === 'image');
  const others = files.filter((f) => getFileKind(f.name, f.contentType) !== 'image');
  const ordered = layout === 'grid' ? [...images, ...others] : [...files];
  const open = (file: FileRef) => setPreviewIndex(ordered.indexOf(file));

  const removeButton = (file: FileRef, className?: string) =>
    onRemove && (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onRemove(file);
        }}
        aria-label={t('files.remove', { name: file.name })}
        className={cn(
          'inline-flex size-6 shrink-0 items-center justify-center rounded-full bg-background/90 text-muted-foreground shadow-sm ring-1 focus-ring ring-border transition-colors hover:text-destructive-text',
          className,
        )}
      >
        <XIcon className="size-3.5" />
      </button>
    );

  const fileRow = (file: FileRef) => (
    <li key={file.id} className="group/file relative">
      <button
        type="button"
        onClick={() => open(file)}
        className="flex w-full min-w-0 items-center gap-3 rounded-lg border bg-card p-2 pr-3 text-left focus-ring transition-colors hover:border-border-strong hover:bg-accent/50"
      >
        <FileIcon name={file.name} contentType={file.contentType} />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium">{file.name}</span>
          <span className="flex items-center gap-2 text-xs text-muted-foreground tabular-nums">
            {fmt.fileSize(file.size)}
            <FileScanBadge file={file} />
          </span>
        </span>
      </button>
      <div className="absolute top-1/2 right-2 flex -translate-y-1/2 items-center gap-1">
        <a
          href={toDownloadUrl(file.url)}
          download={file.name}
          onClick={(e) => e.stopPropagation()}
          aria-label={t('files.download')}
          className="inline-flex size-7 items-center justify-center rounded-md text-muted-foreground focus-ring transition-opacity group-hover/file:opacity-100 hover:bg-background hover:text-foreground focus-visible:opacity-100 can-hover:opacity-0"
        >
          <DownloadIcon className="size-4" />
        </a>
        {removeButton(file)}
      </div>
    </li>
  );

  return (
    <div className={cn('space-y-2', className)} data-slot="attachment-list">
      {layout === 'grid' && images.length > 0 && (
        <ul className="flex flex-wrap gap-2">
          {images.map((file) => (
            <li key={file.id} className="group/file relative">
              <button
                type="button"
                onClick={() => open(file)}
                className="block size-24 overflow-hidden rounded-lg border bg-muted focus-ring sm:size-28"
                aria-label={t('files.preview', { name: file.name })}
              >
                <img
                  src={file.thumbnailUrl ?? file.url}
                  alt={file.name}
                  loading="lazy"
                  className="size-full object-cover transition-transform duration-300 group-hover/file:scale-[1.04]"
                />
              </button>
              <FileScanBadge
                file={file}
                className="absolute bottom-1 left-1 rounded bg-background/90 px-1 py-0.5"
              />
              {removeButton(file, 'absolute -top-2 -right-2')}
            </li>
          ))}
        </ul>
      )}
      {(layout === 'list' ? files : others).length > 0 && (
        <ul className={cn('grid gap-2', layout === 'grid' && 'sm:grid-cols-2')}>
          {(layout === 'list' ? files : others).map(fileRow)}
        </ul>
      )}
      <FilePreviewDialog files={ordered} index={previewIndex} onIndexChange={setPreviewIndex} />
    </div>
  );
}
